import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  Flex,
  Icon,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Spinner,
  IconButton,
  Input,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  useToast,
  Select,
  Checkbox,
} from '@chakra-ui/react';
import { BellIcon, DownloadIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import {
  FaUsers,
  FaUserInjured,
  FaFileMedical,
  FaCheckCircle,
  FaBuilding,
  FaShieldAlt,
  FaVial,
  FaTint,
  FaDollarSign,
  FaClock,
  FaArrowRight,
  FaCreditCard,
  FaUserPlus,
  FaEdit,
  FaArchive,
  FaFileUpload,
  FaSync,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaTimesCircle,
  FaHourglassHalf,
  FaHandHoldingUsd,
} from 'react-icons/fa';
import { formatDistanceToNow, subDays, startOfDay, format } from 'date-fns';
import { supabase } from '../../supabaseClient';

/* ─── Quick Actions Config ──────────────────────────────────────────────── */
const quickActions = [
  { label: 'Manage Phlebotomists', path: '/admin/phlebotomists', icon: FaBuilding, color: 'blue' },
  { label: 'Manage Insurances', path: '/admin/insurances', icon: FaShieldAlt, color: 'purple' },
  { label: 'Manage Test Types', path: '/admin/test-types', icon: FaVial, color: 'teal' },
  { label: 'Blood Draw Dashboard', path: '/admin/blood-draws', icon: FaTint, color: 'green' },
  { label: 'Lab Test Costs', path: '/admin/lab-test-costs', icon: FaDollarSign, color: 'orange' },
];

const ACTIVITY_LIMIT = 15;

/* ─── Activity Icon ─────────────────────────────────────────────────────── */
function getActivityIcon(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('payment') || a.includes('paid') || a.includes('charge')) return FaCreditCard;
  if (a.includes('add') || a.includes('create') || a.includes('upload')) return FaUserPlus;
  if (a.includes('edit') || a.includes('update') || a.includes('restore')) return FaEdit;
  if (a.includes('archive')) return FaArchive;
  if (a.includes('csv') || a.includes('upload')) return FaFileUpload;
  return FaClock;
}

/* ─── Date Range Helpers ────────────────────────────────────────────────── */
function getDateRange(range) {
  const now = new Date();
  switch (range) {
    case '7d': return startOfDay(subDays(now, 7));
    case '30d': return startOfDay(subDays(now, 30));
    case '90d': return startOfDay(subDays(now, 90));
    default: return null; // all time
  }
}

/* ═══════════════════════════════════════════════════════════════════════════ */
function AdminDashboard() {
  const { adminUser } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const emptyStateBg = useColorModeValue('gray.100', 'gray.700');

  /* ─── State ───────────────────────────────────────────────────────────── */
  const [dateRange, setDateRange] = useState('all');
  const [stats, setStats] = useState({ phlebotomists: 0, patients: 0, submissions: 0, completed: 0, revenue: 0, unpaidInvoices: 0, unpaidAmount: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [adminLogs, setAdminLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [labsMap, setLabsMap] = useState({});
  const [activityLoading, setActivityLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // New dashboard sections
  const [failedPayments, setFailedPayments] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [selfPaySubmissions, setSelfPaySubmissions] = useState([]);

  // Export modal
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();
  const [exportType, setExportType] = useState('submissions');
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFields, setExportFields] = useState({});

  // Notification modal
  const { isOpen: isNotifOpen, onOpen: onNotifOpen, onClose: onNotifClose } = useDisclosure();

  /* ─── Fetch Stats ─────────────────────────────────────────────────────── */
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const since = getDateRange(dateRange);
      const sinceISO = since ? since.toISOString() : null;

      // Build queries
      let phlebQ = supabase.from('phlebotomist_profiles').select('id', { count: 'exact', head: true });
      let patientQ = supabase.from('patients').select('id', { count: 'exact', head: true });
      let subQ = supabase.from('submissions').select('id', { count: 'exact', head: true });
      let compQ = supabase.from('submissions').select('id', { count: 'exact', head: true }).eq('status', 'completed');
      let revQ = supabase.from('billing_transactions').select('amount').eq('status', 'completed');
      let unpaidInvQ = supabase.from('submission_invoices').select('id, total_amount', { count: 'exact' }).neq('payment_status', 'paid');

      if (sinceISO) {
        subQ = subQ.gte('submitted_at', sinceISO);
        compQ = compQ.gte('submitted_at', sinceISO);
        revQ = revQ.gte('transaction_date', sinceISO);
      }

      const [phlebRes, patientRes, subRes, compRes, revRes, unpaidRes] = await Promise.all([
        phlebQ, patientQ, subQ, compQ, revQ, unpaidInvQ,
      ]);

      const totalRevenue = (revRes.data || []).reduce((sum, r) => sum + Number(r.amount || 0), 0);
      const unpaidAmount = (unpaidRes.data || []).reduce((sum, r) => sum + Number(r.total_amount || 0), 0);

      setStats({
        phlebotomists: phlebRes.count || 0,
        patients: patientRes.count || 0,
        submissions: subRes.count || 0,
        completed: compRes.count || 0,
        revenue: totalRevenue,
        unpaidInvoices: unpaidRes.count || 0,
        unpaidAmount,
      });
    } catch (err) {
      console.error('Stats fetch error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange]);

  /* ─── Fetch Notifications ─────────────────────────────────────────────── */
  const fetchNotifications = useCallback(async () => {
    try {
      const since24h = subDays(new Date(), 1).toISOString();
      const [newSubRes, newPayRes, newLabRes] = await Promise.all([
        supabase.from('submissions').select('id, patient_name, submitted_at, status').gte('submitted_at', since24h).order('submitted_at', { ascending: false }).limit(10),
        supabase.from('billing_transactions').select('id, amount, description, transaction_date, metadata').eq('status', 'completed').gte('transaction_date', since24h).order('transaction_date', { ascending: false }).limit(10),
        supabase.from('admin_activity_log').select('id, action, username, details, created_at').gte('created_at', since24h).order('created_at', { ascending: false }).limit(10),
      ]);

      const notifs = [];
      (newSubRes.data || []).forEach(s => {
        notifs.push({ id: `sub-${s.id}`, title: 'New Submission', message: `${s.patient_name || 'Unknown patient'} — ${s.status}`, time: s.submitted_at, type: 'submission' });
      });
      (newPayRes.data || []).forEach(p => {
        notifs.push({ id: `pay-${p.id}`, title: 'Payment Received', message: `$${Number(p.amount).toFixed(2)} — ${p.description || ''}`, time: p.transaction_date, type: 'payment' });
      });

      notifs.sort((a, b) => new Date(b.time) - new Date(a.time));
      setNotifications(notifs.slice(0, 20));

      // Read state from localStorage
      const lastRead = localStorage.getItem('admin_notif_last_read');
      const unread = lastRead ? notifs.filter(n => new Date(n.time) > new Date(lastRead)).length : notifs.length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Notifications fetch error:', err);
    }
  }, []);

  /* ─── Fetch Recent Activity ───────────────────────────────────────────── */
  const fetchRecentActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const [logRes, txRes, labsRes] = await Promise.all([
        supabase.from('admin_activity_log').select('id, action, username, details, created_at').order('created_at', { ascending: false }).limit(ACTIVITY_LIMIT),
        supabase.from('billing_transactions').select('id, lab_id, amount, description, transaction_date, status, metadata').eq('status', 'completed').order('transaction_date', { ascending: false }).limit(ACTIVITY_LIMIT),
        supabase.from('labs').select('id, name'),
      ]);
      if (logRes.error) throw logRes.error;
      if (txRes.error) throw txRes.error;
      setAdminLogs(logRes.data || []);
      setPayments(txRes.data || []);
      const labs = labsRes.data || [];
      setLabsMap(Object.fromEntries(labs.map((l) => [String(l.id), l.name])));
    } catch (err) {
      console.error('Recent activity fetch error:', err);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  /* ─── Fetch Failed Payments ───────────────────────────────────────────── */
  const fetchFailedPayments = useCallback(async () => {
    try {
      const since30d = subDays(new Date(), 30).toISOString();
      const { data } = await supabase
        .from('billing_transactions')
        .select('id, lab_id, amount, description, failure_reason, transaction_date, metadata')
        .eq('status', 'failed')
        .gte('transaction_date', since30d)
        .order('transaction_date', { ascending: false })
        .limit(10);
      setFailedPayments(data || []);
    } catch (err) {
      console.error('Failed payments fetch error:', err);
    }
  }, []);

  /* ─── Fetch Overdue Invoices (>30 days unpaid) ──────────────────────── */
  const fetchOverdueInvoices = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('submission_invoices')
        .select('id, invoice_number, total_amount, created_at, payment_status, original_batch_id')
        .neq('payment_status', 'paid')
        .order('created_at', { ascending: true })
        .limit(50);
      const now = new Date();
      const overdue = (data || []).filter(inv => {
        const created = new Date(inv.created_at);
        const daysOld = Math.floor((now - created) / (1000 * 60 * 60 * 24));
        return daysOld > 30;
      }).map(inv => ({
        ...inv,
        daysOld: Math.floor((now - new Date(inv.created_at)) / (1000 * 60 * 60 * 24)),
      }));
      setOverdueInvoices(overdue);
    } catch (err) {
      console.error('Overdue invoices fetch error:', err);
    }
  }, []);

  /* ─── Fetch Self-Pay Submissions ────────────────────────────────────── */
  const fetchSelfPaySubmissions = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('submissions')
        .select('id, draw_code, patient_name, payment_status, self_pay_amount, submitted_at, lab_id')
        .eq('is_self_pay', true)
        .order('submitted_at', { ascending: false })
        .limit(10);
      setSelfPaySubmissions(data || []);
    } catch (err) {
      console.error('Self-pay fetch error:', err);
    }
  }, []);

  /* ─── Initial Fetch ───────────────────────────────────────────────────── */
  useEffect(() => {
    fetchStats();
    fetchNotifications();
    fetchRecentActivity();
    fetchFailedPayments();
    fetchOverdueInvoices();
    fetchSelfPaySubmissions();
  }, [fetchStats, fetchNotifications, fetchRecentActivity, fetchFailedPayments, fetchOverdueInvoices, fetchSelfPaySubmissions]);

  // Poll every 5 minutes instead of refreshing on every focus/tab switch
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRecentActivity();
      fetchNotifications();
    }, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, [fetchRecentActivity, fetchNotifications]);

  /* ─── Recent Activity Memo ────────────────────────────────────────────── */
  const recentActivity = useMemo(() => {
    const items = [];
    (adminLogs || []).forEach((row) => {
      items.push({ id: `log-${row.id}`, type: 'activity', title: row.action, subtitle: row.details ? `${row.username}: ${row.details}` : row.username, at: row.created_at });
    });
    (payments || []).forEach((row) => {
      const labKey = row.lab_id ? String(row.lab_id) : null;
      const labLabel = row.metadata?.lab_name || (labKey ? (labsMap[labKey] || 'Unknown lab') : '(Unassigned)');
      const amount = `$${Number(row.amount).toFixed(2)}`;
      const desc = row.description || '';
      const markedBy = row.metadata?.marked_by || (desc.match(/\s*Marked by:\s*(.+?)(?:\.|$)/i)?.[1]?.trim());
      const descShort = desc ? desc.replace(/\s*Marked by:.*$/i, '').trim() : '';
      items.push({ id: `tx-${row.id}`, type: 'payment', title: 'Payment received', subtitle: `Lab: ${labLabel} — ${amount}${descShort ? ` · ${descShort}` : ''}`, markedBy: markedBy || null, at: row.transaction_date });
    });
    items.sort((a, b) => new Date(b.at) - new Date(a.at));
    return items.slice(0, ACTIVITY_LIMIT);
  }, [adminLogs, payments, labsMap]);

  /* ─── Mark Notifications Read ─────────────────────────────────────────── */
  const handleOpenNotifications = () => {
    onNotifOpen();
    localStorage.setItem('admin_notif_last_read', new Date().toISOString());
    setUnreadCount(0);
  };

  /* ─── Export ──────────────────────────────────────────────────────────── */
  const exportFieldOptions = {
    submissions: ['id', 'draw_code', 'patient_name', 'patient_email', 'patient_dob', 'patient_gender', 'doctor_name', 'status', 'submitted_at', 'blood_collection_time', 'phlebotomist_name', 'special_instructions'],
    billing: ['id', 'lab_id', 'amount', 'description', 'transaction_date', 'status'],
    doctors: ['id', 'full_name', 'email', 'phone', 'clinic_name', 'agreement_signed'],
    phlebotomists: ['id', 'full_name', 'company_name', 'email', 'phone', 'agreement_signed'],
  };

  const exportTableNames = {
    submissions: 'submissions',
    billing: 'billing_transactions',
    doctors: 'doctors',
    phlebotomists: 'phlebotomist_profiles',
  };

  useEffect(() => {
    // Select all fields by default when export type changes
    const fields = exportFieldOptions[exportType] || [];
    const allSelected = {};
    fields.forEach(f => { allSelected[f] = true; });
    setExportFields(allSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportType]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const selectedFields = Object.keys(exportFields).filter(k => exportFields[k]);
      if (selectedFields.length === 0) { setExportLoading(false); return; }

      let query = supabase.from(exportTableNames[exportType]).select(selectedFields.join(', '));

      // Date filter
      const dateField = exportType === 'submissions' ? 'submitted_at' : exportType === 'billing' ? 'transaction_date' : null;
      if (dateField && exportDateFrom) query = query.gte(dateField, new Date(exportDateFrom).toISOString());
      if (dateField && exportDateTo) query = query.lte(dateField, new Date(exportDateTo + 'T23:59:59').toISOString());

      query = query.order(dateField || 'id', { ascending: false }).limit(10000);
      const { data, error } = await query;
      if (error) {
        // Handle missing table gracefully
        if (error.message?.includes('does not exist') || error.code === '42P01') {
          toast({ title: 'No data available', description: `The ${exportType} table has no data yet.`, status: 'info', duration: 3000 });
          setExportLoading(false);
          return;
        }
        throw error;
      }
      if (!data || data.length === 0) {
        toast({ title: 'No data found', description: 'Try adjusting your date range or removing the date filter.', status: 'info', duration: 3000 });
        setExportLoading(false);
        return;
      }

      // Format date/time fields for readability
      const dateFields = ['submitted_at', 'created_at', 'updated_at', 'transaction_date', 'shipped_at', 'invoice_generated_at', 'agreement_signed', 'last_accessed'];
      const formatExportValue = (key, val) => {
        if (val == null || val === '') return '';
        if (dateFields.includes(key) || (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val))) {
          const d = new Date(val);
          if (!isNaN(d.getTime())) {
            return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
                   d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
          }
        }
        return String(val);
      };

      // Convert to CSV
      const headers = selectedFields;
      const csvRows = [headers.join(',')];
      data.forEach(row => {
        const values = headers.map(h => {
          const str = formatExportValue(h, row[h]).replace(/"/g, '""');
          return `"${str}"`;
        });
        csvRows.push(values.join(','));
      });
      const csv = csvRows.join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportType}_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      onExportClose();
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  /* ─── Stat Cards Config ───────────────────────────────────────────────── */
  const statCards = [
    { label: 'Total Phlebotomists', value: stats.phlebotomists, icon: FaUsers, color: 'blue' },
    { label: 'Total Patients', value: stats.patients, icon: FaUserInjured, color: 'teal' },
    { label: 'Total Submissions', value: stats.submissions, icon: FaFileMedical, color: 'purple' },
    { label: 'Completed', value: stats.completed, icon: FaCheckCircle, color: 'green' },
    { label: 'Revenue', value: `$${stats.revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: FaDollarSign, color: 'orange' },
    { label: 'Unpaid Invoices', value: stats.unpaidInvoices, subtitle: stats.unpaidAmount > 0 ? `$${stats.unpaidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} outstanding` : null, icon: FaFileInvoiceDollar, color: 'red' },
  ];

  const dateRangeLabel = { all: 'All Time', '7d': 'Last 7 Days', '30d': 'Last 30 Days', '90d': 'Last 90 Days' };

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Box px={{ base: 4, md: 8 }} py={8} bg={useColorModeValue('gray.50', 'gray.900')} minH="100vh">
      {/* ─── Header Row ─────────────────────────────────────────────────── */}
      <Flex justify="space-between" align="flex-start" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Heading as="h1" size="xl" fontWeight="700" color={useColorModeValue('gray.800', 'white')} mb={1}>
            Welcome{adminUser?.name ? `, ${adminUser.name}` : ''}
          </Heading>
          <Text color={mutedColor} fontSize="md">
            Here's what's happening across your platform.
          </Text>
        </Box>
        <HStack spacing={3}>
          {/* Notification Bell */}
          <Box position="relative">
            <IconButton
              aria-label="Notifications"
              icon={<BellIcon boxSize={5} />}
              variant="outline"
              size="md"
              onClick={handleOpenNotifications}
            />
            {unreadCount > 0 && (
              <Badge
                colorScheme="red"
                borderRadius="full"
                position="absolute"
                top="-1"
                right="-1"
                fontSize="xs"
                minW="18px"
                textAlign="center"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Box>
          {/* Export Button */}
          <Button leftIcon={<DownloadIcon />} variant="outline" size="md" onClick={onExportOpen}>
            Export
          </Button>
        </HStack>
      </Flex>

      {/* ─── Date Range + Stats ─────────────────────────────────────────── */}
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontWeight="600" color={mutedColor} fontSize="sm">Overview</Text>
        <Menu>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<ChevronDownIcon />}>
            {dateRangeLabel[dateRange]}
          </MenuButton>
          <MenuList>
            {Object.entries(dateRangeLabel).map(([key, label]) => (
              <MenuItem key={key} onClick={() => setDateRange(key)} fontWeight={dateRange === key ? '600' : '400'}>
                {label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 6 }} spacing={4} mb={8}>
        {statCards.map((stat) => (
          <Card key={stat.label} bg={cardBg} borderWidth="1px" borderColor={borderColor} shadow="sm" overflow="hidden">
            <CardBody py={4} px={4}>
              <Flex justify="space-between" align="flex-start">
                <Box>
                  <Text fontSize="xs" color={mutedColor} fontWeight="500" mb={1}>
                    {stat.label}
                  </Text>
                  <Heading size="md" fontWeight="700">
                    {statsLoading ? <Spinner size="sm" /> : stat.value}
                  </Heading>
                  {stat.subtitle && !statsLoading && (
                    <Text fontSize="xs" color="red.500" mt={1}>{stat.subtitle}</Text>
                  )}
                </Box>
                <Box bg={`${stat.color}.50`} color={`${stat.color}.600`} p={2} borderRadius="lg">
                  <Icon as={stat.icon} boxSize={4} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* ─── Quick Actions + Recent Activity ────────────────────────────── */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} shadow="sm">
          <CardHeader pb={2}>
            <Heading size="md" fontWeight="600">Quick Actions</Heading>
            <Text fontSize="sm" color={mutedColor}>Jump to key admin areas</Text>
          </CardHeader>
          <CardBody pt={0}>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
              {quickActions.map((action) => (
                <Button
                  key={action.path}
                  variant="ghost"
                  size="md"
                  h="auto"
                  py={3}
                  px={3}
                  justifyContent="flex-start"
                  alignItems="center"
                  onClick={() => navigate(action.path)}
                  colorScheme={action.color}
                  _hover={{ bg: hoverBg }}
                  leftIcon={<Icon as={action.icon} boxSize={4} />}
                  rightIcon={<Icon as={FaArrowRight} boxSize={3} opacity={0.6} />}
                  fontWeight="500"
                  fontSize="sm"
                >
                  {action.label}
                </Button>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} shadow="sm">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Heading size="md" fontWeight="600">Recent Activity</Heading>
                <Text fontSize="sm" color={mutedColor}>Latest actions and events</Text>
              </Box>
              <IconButton
                aria-label="Refresh activity"
                icon={<Icon as={FaSync} />}
                size="sm"
                variant="ghost"
                onClick={fetchRecentActivity}
                isLoading={activityLoading}
              />
            </Flex>
          </CardHeader>
          <CardBody pt={0}>
            {activityLoading ? (
              <Flex py={8} justify="center"><Spinner size="lg" /></Flex>
            ) : recentActivity.length === 0 ? (
              <VStack py={8} spacing={3}>
                <Box bg={emptyStateBg} color={mutedColor} p={4} borderRadius="full">
                  <Icon as={FaClock} boxSize={8} />
                </Box>
                <Text color={mutedColor} fontSize="sm" textAlign="center">No recent activity to display</Text>
                <Text color={mutedColor} fontSize="xs" textAlign="center" maxW="240px">
                  Activity from admin actions and payments will appear here.
                </Text>
              </VStack>
            ) : (
              <Box maxH="360px" overflowY="auto" overflowX="hidden">
                <VStack align="stretch" spacing={0} divider={<Box h="1px" bg={borderColor} />}>
                  {recentActivity.map((item) => (
                    <Flex key={item.id} py={3} gap={3} align="flex-start">
                      <Box flexShrink={0} mt={0.5} color={item.type === 'payment' ? 'green.500' : item.type === 'failed' ? 'red.500' : 'blue.500'}>
                        <Icon as={item.type === 'payment' ? FaCreditCard : item.type === 'failed' ? FaTimesCircle : getActivityIcon(item.title)} boxSize={4} />
                      </Box>
                      <Box flex={1} minW={0}>
                        <HStack spacing={2}>
                          <Text fontSize="sm" fontWeight="500" noOfLines={1}>{item.title}</Text>
                          {item.type === 'payment' && <Badge colorScheme="green" fontSize="2xs">Paid</Badge>}
                          {item.type === 'failed' && <Badge colorScheme="red" fontSize="2xs">Failed</Badge>}
                        </HStack>
                        <Text fontSize="xs" color={mutedColor} noOfLines={2}>{item.subtitle}</Text>
                        {item.markedBy && (
                          <Text fontSize="xs" color="gray.600" fontWeight="500" mt={0.5}>
                            Marked by: {item.markedBy}
                          </Text>
                        )}
                        <Text fontSize="xs" color={mutedColor} mt={0.5}>
                          {formatDistanceToNow(new Date(item.at), { addSuffix: true })}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* ─── Alerts Row: Failed Payments + Overdue Invoices ────────────── */}
      {(failedPayments.length > 0 || overdueInvoices.length > 0) && (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={6}>
          {/* Failed Payments */}
          {failedPayments.length > 0 && (
            <Card bg={cardBg} borderWidth="2px" borderColor="red.200" shadow="sm">
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaTimesCircle} color="red.500" />
                  <Heading size="md" fontWeight="600" color="red.600">Failed Payments</Heading>
                  <Badge colorScheme="red" borderRadius="full">{failedPayments.length}</Badge>
                </HStack>
                <Text fontSize="sm" color={mutedColor}>Last 30 days — needs attention</Text>
              </CardHeader>
              <CardBody pt={0}>
                <Box maxH="240px" overflowY="auto">
                  <VStack align="stretch" spacing={0} divider={<Box h="1px" bg={borderColor} />}>
                    {failedPayments.map((fp) => (
                      <Flex key={fp.id} py={2.5} gap={3} align="center">
                        <Icon as={FaExclamationTriangle} color="red.400" boxSize={3.5} flexShrink={0} />
                        <Box flex={1} minW={0}>
                          <HStack spacing={2}>
                            <Text fontSize="sm" fontWeight="600" color="red.600">
                              ${Number(fp.amount || 0).toFixed(2)}
                            </Text>
                            <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                              {labsMap[String(fp.lab_id)] || fp.metadata?.lab_name || 'Unknown lab'}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="red.500" noOfLines={1}>
                            {fp.failure_reason || fp.description || 'Payment declined'}
                          </Text>
                          <Text fontSize="xs" color={mutedColor}>
                            {formatDistanceToNow(new Date(fp.transaction_date), { addSuffix: true })}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </CardBody>
            </Card>
          )}

          {/* Overdue Invoices */}
          {overdueInvoices.length > 0 && (
            <Card bg={cardBg} borderWidth="2px" borderColor="orange.200" shadow="sm">
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaHourglassHalf} color="orange.500" />
                  <Heading size="md" fontWeight="600" color="orange.600">Overdue Invoices</Heading>
                  <Badge colorScheme="orange" borderRadius="full">{overdueInvoices.length}</Badge>
                </HStack>
                <Text fontSize="sm" color={mutedColor}>Unpaid for more than 30 days</Text>
              </CardHeader>
              <CardBody pt={0}>
                <Box maxH="240px" overflowY="auto">
                  <VStack align="stretch" spacing={0} divider={<Box h="1px" bg={borderColor} />}>
                    {overdueInvoices.map((inv) => (
                      <Flex key={inv.id} py={2.5} gap={3} align="center" cursor="pointer" _hover={{ bg: hoverBg }} borderRadius="md" onClick={() => navigate('/admin/invoices')}>
                        <Box flex={1} minW={0}>
                          <HStack spacing={2}>
                            <Text fontSize="sm" fontWeight="600">
                              {inv.invoice_number || inv.id.slice(0, 8)}
                            </Text>
                            <Text fontSize="sm" fontWeight="700" color="orange.600">
                              ${Number(inv.total_amount || 0).toFixed(2)}
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Badge colorScheme={inv.daysOld > 60 ? 'red' : 'orange'} fontSize="2xs">
                              {inv.daysOld} days overdue
                            </Badge>
                            <Text fontSize="xs" color={mutedColor}>
                              Created {format(new Date(inv.created_at), 'MMM d, yyyy')}
                            </Text>
                          </HStack>
                        </Box>
                        <Icon as={FaArrowRight} color={mutedColor} boxSize={3} />
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </CardBody>
            </Card>
          )}
        </SimpleGrid>
      )}

      {/* ─── Self-Pay Tracking ─────────────────────────────────────────── */}
      {selfPaySubmissions.length > 0 && (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} shadow="sm" mt={6}>
          <CardHeader pb={2}>
            <HStack>
              <Icon as={FaHandHoldingUsd} color="teal.500" />
              <Heading size="md" fontWeight="600">Self-Pay Patients</Heading>
              <Badge colorScheme="teal" borderRadius="full">{selfPaySubmissions.length}</Badge>
            </HStack>
            <Text fontSize="sm" color={mutedColor}>Recent self-pay submissions and payment status</Text>
          </CardHeader>
          <CardBody pt={0}>
            <Box overflowX="auto">
              <Box as="table" w="full" fontSize="sm">
                <Box as="thead">
                  <Box as="tr" borderBottom="2px" borderColor={borderColor}>
                    <Box as="th" textAlign="left" py={2} px={3} fontWeight="600" color={mutedColor} fontSize="xs" textTransform="uppercase">Patient</Box>
                    <Box as="th" textAlign="left" py={2} px={3} fontWeight="600" color={mutedColor} fontSize="xs" textTransform="uppercase">Draw Code</Box>
                    <Box as="th" textAlign="left" py={2} px={3} fontWeight="600" color={mutedColor} fontSize="xs" textTransform="uppercase">Amount</Box>
                    <Box as="th" textAlign="left" py={2} px={3} fontWeight="600" color={mutedColor} fontSize="xs" textTransform="uppercase">Status</Box>
                    <Box as="th" textAlign="left" py={2} px={3} fontWeight="600" color={mutedColor} fontSize="xs" textTransform="uppercase">Submitted</Box>
                  </Box>
                </Box>
                <Box as="tbody">
                  {selfPaySubmissions.map((sub) => {
                    const status = sub.payment_status || 'unpaid';
                    const statusColor = status === 'paid' ? 'green' : status === 'processing' ? 'blue' : status === 'cancelled' ? 'red' : 'orange';
                    return (
                      <Box as="tr" key={sub.id} borderBottom="1px" borderColor={borderColor} _hover={{ bg: hoverBg }} cursor="pointer" onClick={() => navigate(`/admin/submissions/${sub.id}`)}>
                        <Box as="td" py={2.5} px={3} fontWeight="500">{sub.patient_name || '—'}</Box>
                        <Box as="td" py={2.5} px={3}><Badge variant="outline" colorScheme="gray" fontFamily="mono" fontSize="xs">{sub.draw_code || '—'}</Badge></Box>
                        <Box as="td" py={2.5} px={3} fontWeight="600">{sub.self_pay_amount ? `$${Number(sub.self_pay_amount).toFixed(2)}` : 'TBD'}</Box>
                        <Box as="td" py={2.5} px={3}><Badge colorScheme={statusColor} fontSize="xs">{status.toUpperCase()}</Badge></Box>
                        <Box as="td" py={2.5} px={3} color={mutedColor}>{sub.submitted_at ? formatDistanceToNow(new Date(sub.submitted_at), { addSuffix: true }) : '—'}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* ═══ Notification Center Modal ═══════════════════════════════════ */}
      <Modal isOpen={isNotifOpen} onClose={onNotifClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <BellIcon />
              <Text>Notifications</Text>
              <Badge colorScheme="blue" fontSize="xs">{notifications.length}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} maxH="500px" overflowY="auto">
            {notifications.length === 0 ? (
              <VStack py={8} spacing={3}>
                <Icon as={BellIcon} boxSize={10} color="gray.300" />
                <Text color={mutedColor} fontSize="sm">No notifications in the last 24 hours</Text>
              </VStack>
            ) : (
              <VStack align="stretch" spacing={0} divider={<Divider />}>
                {notifications.map((n) => (
                  <Flex key={n.id} py={3} gap={3} align="flex-start">
                    <Box flexShrink={0} mt={0.5} color={n.type === 'payment' ? 'green.500' : 'blue.500'}>
                      <Icon as={n.type === 'payment' ? FaCreditCard : FaFileMedical} boxSize={4} />
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="600">{n.title}</Text>
                      <Text fontSize="xs" color={mutedColor}>{n.message}</Text>
                      <Text fontSize="xs" color={mutedColor} mt={0.5}>
                        {formatDistanceToNow(new Date(n.time), { addSuffix: true })}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ═══ Export Modal ════════════════════════════════════════════════ */}
      <Modal isOpen={isExportOpen} onClose={onExportClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <DownloadIcon />
              <Text>Export Data</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="600" mb={2} fontSize="sm">Data Type</Text>
                <Select value={exportType} onChange={(e) => setExportType(e.target.value)}>
                  <option value="submissions">Submissions</option>
                  <option value="billing">Billing Transactions</option>
                  <option value="doctors">Doctors</option>
                  <option value="phlebotomists">Phlebotomists</option>
                </Select>
              </Box>
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontWeight="600" mb={2} fontSize="sm">From Date</Text>
                  <Input type="date" value={exportDateFrom} onChange={(e) => setExportDateFrom(e.target.value)} />
                </Box>
                <Box>
                  <Text fontWeight="600" mb={2} fontSize="sm">To Date</Text>
                  <Input type="date" value={exportDateTo} onChange={(e) => setExportDateTo(e.target.value)} />
                </Box>
              </SimpleGrid>
              <Box>
                <Text fontWeight="600" mb={2} fontSize="sm">Fields to Export</Text>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                  {(exportFieldOptions[exportType] || []).map(field => (
                    <Checkbox
                      key={field}
                      isChecked={exportFields[field] || false}
                      onChange={(e) => setExportFields(prev => ({ ...prev, [field]: e.target.checked }))}
                      size="sm"
                    >
                      {field.replace(/_/g, ' ')}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onExportClose}>Cancel</Button>
            <Button
              colorScheme="blue"
              leftIcon={<DownloadIcon />}
              onClick={handleExport}
              isLoading={exportLoading}
              isDisabled={Object.values(exportFields).filter(Boolean).length === 0}
            >
              Download CSV
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default AdminDashboard;
